import { useTranslation } from "react-i18next"
import { CustomPaper } from "../../../../../../components/CustomPaper"
import { Box, Button, Grid, IconButton, Tooltip, Typography } from "@mui/material"
import { KeyCaptionVertical } from "../../../../../../components/detailKeyValue/keyCaption.index"
import { icons } from "../../../../../../utils/icons"
import { useStyles } from "./styles"
import { DatFileProps } from "./types"
import { SnackSimple } from "../../../../../../components"
import SimpleModal from "../../../../../../components/modal/simpleModal"
import { useDropzone } from "react-dropzone"
import { useEffect, useRef, useState } from "react"
import { Types } from "../../../../../../utils/enums"
import { allowedFormats } from "../../../../utils/constans/formats"
import { isFileSizeValid } from "../../../main/components/allFiles/constans/validationsFile"
import { formatFileSize, getReadableFileType } from "../../../createFile/components/methos"
import dayjs from "dayjs"
import { ViewFile } from "../viewFile"



export const DataFile = ({ keyFile, canSee, file, isEdit, setFile, canDownload }: DatFileProps) => {

    const { t } = useTranslation()
    const styles = useStyles()
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [viewFile, setViewFile] = useState<{ fileKey: string, fileName: string } | null>(null);
    const [fileLocalDocument, setFileLocalDocument] = useState(false)

    const [dataFile, setDataFile] = useState<{ type: string; size: string; createDate: string; editDate: string }>({
        type: '',
        size: '',
        createDate: '',
        editDate: ''
    });

    const [openSnackbar, setOpenSnackbar] = useState({
        open: false,
        message: '',
        type: Types.SUCCESS,
    })

    const [modal, setModal] = useState<{
        open: boolean
        title: string
        description?: string
        children?: JSX.Element
        removeCancel?: boolean
        confirm: () => void
    }>({
        open: false,
        title: '',
        description: '',
        confirm: () => { },
    })

    const closeSnackbar = () => { setOpenSnackbar((prev) => ({ ...prev, open: false })) }

    const closeModal = () => {
        setModal(prev => ({
            ...prev,
            open: false,
        }))
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: false,
        accept: Object.keys(allowedFormats).reduce((acc, ext) => {
            acc[`.${ext}`] = [];
            return acc;
        }, {} as Record<string, string[]>),
        onDrop: acceptedFiles => {
            if (acceptedFiles.length > 0) {
                const newFile = acceptedFiles[0];

                // Verificación extra por extensión (por si el navegador no la valida bien)
                const fileExt = newFile.name.split('.').pop()?.toLowerCase();
                if (!fileExt || !allowedFormats[fileExt as keyof typeof allowedFormats]) {
                    setOpenSnackbar({
                        open: true,
                        message: t('filesManager.snackbar.unsupportedFileType'),
                        type: Types.ERROR,
                    });
                    return;
                }

                const { valid, category, max } = isFileSizeValid(newFile);

                if (!valid) {
                    setOpenSnackbar((prev) => ({
                        ...prev,
                        message: t(`filesManager.snackbar.theFileIsTooLarge_${category}`, { max }),
                        type: Types.ERROR,
                        open: true,
                    }))
                    return;
                }

                //para ajustar el nombre
                const sanitizedFileName = newFile.name.replace(/\s+/g, '_');
                const renamedFile = new File([newFile], sanitizedFileName, {
                    type: newFile.type,
                    lastModified: newFile.lastModified,
                });

                if (file) {
                    setModal(prev => ({
                        ...prev,
                        open: true,
                        title: t('filesManager.modal.titles.youWantToReplaceThisFile'),
                        confirm: () => {
                            setFile(renamedFile)
                            closeModal();
                        }
                    }));
                } else {
                    // No hay archivo, subir directamente
                    setFile(renamedFile);
                }

            }
        }
    });

    const handleLoad = async () => {
        if (!file) return;

        // Caso 1: archivo proveniente del servidor (FileData)
        if ("url" in file && typeof file.url === "string") {
            const link = document.createElement('a');
            link.href = file.url;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        // Caso 2: archivo cargado por el usuario (File)
        else if (file instanceof File) {
            const blobUrl = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        }
    }

    useEffect(() => {
        if (file) {
            const now = dayjs().format("DD/MM/YYYY hh:mm A");

            if (file instanceof File) {
                // archivo cargado por usuario → fechas actuales
                setDataFile(prev => ({
                    ...prev,
                    type: getReadableFileType(file),
                    size: formatFileSize(file.size),
                    editDate: now,
                }));
            } else {
                // archivo desde backend (FileData)
                setDataFile({
                    type: file.type,
                    size: file.size,
                    createDate: file.createDate,
                    editDate: file.editDate,
                });
            }
        }
    }, [file]);

    useEffect(() => {
        if (file && file instanceof File) {
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (!ext) return;

            // Para PDF, TXT, CSV, DOCX, XLSX, PPTX → usar iframe
            if (['pdf', 'txt', 'csv', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
                setFileLocalDocument(true)
            } else {
                setFileLocalDocument(false)
            }
        }
    }, [file]);

    return (

        <Box sx={[styles.flex1, styles.mainContainer]}>
            <Typography variant="title_grey" fontSize={16}>{t('filesManager.titles.file')}</Typography>

            <CustomPaper elevation={3} sx={styles.container}>
                <Box sx={styles.containerFileData} {...(isEdit ? getRootProps() : {})}>
                    {isEdit && <input {...getInputProps()} ref={inputRef} />}
                    <Box sx={styles.items}>
                        <Tooltip title={file.name} placement="top" arrow>
                            <Typography
                                className="bold full-w title-three"
                                sx={styles.itemTitle(false/* canSee && !fileLocalDocument && isEdit */)}
                            >
                                {file.name ?? ''}
                            </Typography>
                        </Tooltip>
                        <Box sx={styles.iconContainer(false/* canSee && !fileLocalDocument && isEdit */)}>
                            {isEdit && <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    inputRef.current?.click();
                                }}
                                className="download"
                            >
                                {icons('swapHoriz')}
                            </Button>}

                            {canDownload && <Button
                                className="download"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleLoad();
                                    //onDeleteFile()
                                }}
                            >
                                {icons('download')}
                            </Button>}

                            {(canSee && !fileLocalDocument && !isEdit) && <Button
                                className="download"
                                onClick={() => setViewFile({ fileKey: keyFile, fileName:file.name })}
                            >
                                {icons('visibility')}
                            </Button>}
                        </Box>

                    </Box>
                </Box>

                <Box sx={styles.flex1}>
                    <Typography variant="title_grey" fontSize={16}>{t('filesManager.titles.aboutTheFile')}</Typography>

                    <Grid container>
                        <Grid item xs={12} sm={12} md={6} lg={12} xl={6}>
                            <KeyCaptionVertical captionProps={{ sx: { width: '48%' } }} valueProps={{ sx: { width: '50%' } }} value={t('filesManager.tags.type')} caption={dataFile.type} />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6} lg={12} xl={6}>
                            <KeyCaptionVertical captionProps={{ sx: { width: '48%' } }} valueProps={{ sx: { width: '50%' } }} value={t('filesManager.tags.size')} caption={dataFile.size} />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6} lg={12} xl={6}>
                            <KeyCaptionVertical captionProps={{ sx: { width: '48%' } }} valueProps={{ sx: { width: '50%' } }} value={t('filesManager.tags.createDate')} caption={dataFile.createDate} />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6} lg={12} xl={6}>
                            <KeyCaptionVertical captionProps={{ sx: { width: '48%' } }} valueProps={{ sx: { width: '50%' } }} value={t('filesManager.tags.editDate')} caption={dataFile.editDate} />
                        </Grid>
                    </Grid>
                </Box>
            </CustomPaper>

            <SnackSimple close={closeSnackbar} open={openSnackbar.open} type={openSnackbar.type} message={openSnackbar.message} />

            <SimpleModal
                open={!!modal.open}
                next={modal.confirm}
                back={closeModal}
                close={closeModal}
                description={modal.description}
                buttonText={t('general.confirm')}
                cancelText={t('general.cancel')}
                title={modal.title}
                loadingButton={false}
            />

            {viewFile && (
                <ViewFile
                    fileKey={viewFile.fileKey}
                    fileName={viewFile.fileName}
                    setViewFile={setViewFile}
                    file={file instanceof File ? file : undefined}
                />
            )}
        </Box>
    )
}